/// <amd-module name="Core/Medius.Core.Web/Scripts/Medius/apps/inbox/InboxAppInsights"/>

import { trackEvent, trackMetric } from 'Core/Medius.Core.Web/Scripts/appInsights';
import { getTenantId } from 'Core/Medius.Core.Web/Scripts/Medius/lib/path';
import { getGlobalId } from 'Core/Medius.Core.Web/Scripts/Medius/lib/utils/user';
import { newGuid } from 'Core/Medius.Core.Web/Scripts/lib/uniqueGuid';

let sessionId: string | null = null;

export enum InboxEventType {
    ToggleToNew = 'NewSupplierInbox_ToggleToNew',
    ToggleBackToOld = 'NewSupplierInbox_ToggleBackToOld',
    SessionStart = 'NewSupplierInbox_SessionStart',
    SessionEnd = 'NewSupplierInbox_SessionEnd',
    DocumentOpened = 'NewSupplierInbox_DocumentOpened',
    DocumentAction = 'NewSupplierInbox_DocumentAction',
    FilterApplied = 'NewSupplierInbox_FilterApplied',
    SavedViewCreated = 'NewSupplierInbox_SavedViewCreated',
    TabSwitch = 'NewSupplierInbox_TabSwitch',
    SessionStorageException = 'NewSupplierInbox_SessionStorageException'
}

interface InboxEventProperties {
    TenantId: string;
    userGlobalId: string;
    [key: string]: any;
}

function getBaseProperties(): InboxEventProperties {
    return {
        TenantId: getTenantId(),
        userGlobalId: getGlobalId()
    };
}

function trackToggleToNew(): void {
    trackEvent({
        name: InboxEventType.ToggleToNew,
        properties: {
            ...getBaseProperties()
        }
    });
}

function trackToggleBackToOld(): void {
    trackEvent({
        name: InboxEventType.ToggleBackToOld,
        properties: {
            ...getBaseProperties()
        }
    });
}

function trackSessionStart(): void {
    trackSessionEnd();
    sessionId = newGuid();
    trackEvent({
        name: InboxEventType.SessionStart,
        properties: {
            ...getBaseProperties(),
            sessionId: sessionId
        }
    });
}

function trackSessionEnd(): void {
    if (sessionId) {
        trackEvent({
            name: InboxEventType.SessionEnd,
            properties: {
                ...getBaseProperties(),
                sessionId: sessionId
            }
        });
    }
    sessionId = null;
}

function trackDocumentOpened(documentId: number): void {
    if (sessionId) {
        trackEvent({
            name: InboxEventType.DocumentOpened,
            properties: {
                ...getBaseProperties(),
                sessionId: sessionId,
                documentId: documentId
            }
        });
    }
}

function trackDocumentAction(documentId: number): void {
    if (sessionId) {
        trackEvent({
            name: InboxEventType.DocumentAction,
            properties: {
                ...getBaseProperties(),
                sessionId: sessionId,
                documentId: documentId
            }
        });
    }
}

function trackFilterApplied(appliedFilterNames: string[]): void {
    if (sessionId) {
        appliedFilterNames.forEach(filterName => {
            trackEvent({
                name: InboxEventType.FilterApplied,
                properties: {
                    ...getBaseProperties(),
                    sessionId: sessionId,
                    filterName: filterName
                }
            });
        });
    }
}

function trackSavedViewCreated(viewName: string): void {
    if (sessionId) {
        trackEvent({
            name: InboxEventType.SavedViewCreated,
            properties: {
                ...getBaseProperties(),
                sessionId: sessionId,
                viewName: viewName
            }
        });
    }
}

function trackTabSwitch(fromTab: string, toTab: string): void {
    if (sessionId) {
        trackEvent({
            name: InboxEventType.TabSwitch,
            properties: {
                ...getBaseProperties(),
                sessionId: sessionId,
                fromTab: fromTab,
                toTab: toTab
            }
        });
    }
}

export enum InboxMetricType {
    FCP = 'NewSupplierInbox_FCP',
    LCP = 'NewSupplierInbox_LCP',
    TTFB = 'NewSupplierInbox_TTFB'
}

function trackInboxFCP(value: number): void {
    trackMetric({ name: InboxMetricType.FCP, average: value });
}

function trackInboxLCP(value: number): void {
    trackMetric({ name: InboxMetricType.LCP, average: value });
}

function trackInboxTTFB(value: number): void {
    trackMetric({ name: InboxMetricType.TTFB, average: value });
}

function trackException(operation: string, error: unknown): void {
    if (sessionId) {
        trackEvent({
            name: InboxEventType.SessionStorageException,
            properties: {
                ...getBaseProperties(),
                sessionId: sessionId,
                operation: operation,
                error: error
            }
        });
    }
}

export type InboxAppInsights = {
    trackToggleToNew: typeof trackToggleToNew;
    trackToggleBackToOld: typeof trackToggleBackToOld;
    trackSessionStart: typeof trackSessionStart;
    trackSessionEnd: typeof trackSessionEnd;
    trackDocumentOpened: typeof trackDocumentOpened;
    trackDocumentAction: typeof trackDocumentAction;
    trackFilterApplied: typeof trackFilterApplied;
    trackSavedViewCreated: typeof trackSavedViewCreated;
    trackTabSwitch: typeof trackTabSwitch;
    trackException: typeof trackException;
    trackInboxFCP: typeof trackInboxFCP;
    trackInboxLCP: typeof trackInboxLCP;
    trackInboxTTFB: typeof trackInboxTTFB;
};

export const inboxAppInsights: InboxAppInsights = {
    trackToggleToNew,
    trackToggleBackToOld,
    trackSessionStart,
    trackSessionEnd,
    trackDocumentOpened,
    trackDocumentAction,
    trackFilterApplied,
    trackSavedViewCreated,
    trackTabSwitch,
    trackException,
    trackInboxFCP,
    trackInboxLCP,
    trackInboxTTFB
};
